"use client";

import { Time, UTCTimestamp } from "lightweight-charts";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import LineChart from "@/components/global/LineChart";
import { Skeleton } from "@/components/ui/skeleton";
import { applyLocalTimezoneOffset } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import { getAssetsHistoricalData } from "@/services/controllers/smart-allocation";
import { useAppSelector } from "@/services/hooks/redux.hooks";

import { chartDataType } from "./types";
import { Asset } from "../types";

export default function SmartAllocationBackTestChart({
  from,
  to,
  isLoading: loading,
  customDuration,
  initialValue,
  actions,
}: {
  from: Date;
  to: Date;
  isLoading: boolean;
  customDuration: string;
  initialValue: number;
  actions?: ReactNode;
}) {
  const { smartAllocation: SA } = useAppSelector(
    (state) => state.smartAllocation,
  );

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [currentWeightsData, setCurrentWeightsData] = useState<chartDataType[]>(
    [],
  );
  const [setWeightsData, setSetWeightsData] = useState<chartDataType[]>([]);
  const [assetsHistoricalData, setAssetsHistoricalData] = useState<{
    [k: string]: chartDataType[];
  }>();
  const [validSymbols, setValidSymbols] = useState<String[]>([]);

  const getAssetSymbol = useCallback((holding: Asset) => {
    const exchange = "BINANCE";
    if (
      holding.asset_details?.asset_name?.toLowerCase() !==
        "USDT".toLowerCase() &&
      holding.asset_details?.asset_name
    ) {
      return `${holding.asset_details?.asset_name}/usd:${exchange}`;
    } else {
      return `${holding.name}/usd`;
    }
  }, []);

  useEffect(() => {
    if (isLoading && !SA?.assets.length) {
      setSetWeightsData([]);
      setCurrentWeightsData([]);
      setAssetsHistoricalData(undefined);
      setValidSymbols([]);
    }
  }, [SA?.assets.length, isLoading]);

  useEffect(() => {
    const controller = new AbortController();
    if (SA?.assets.length) {
      const symbols = SA?.assets.map(getAssetSymbol).sort();
      setIsLoading(true);
      getAssetsHistoricalData(
        symbols,
        customDuration,
        new Date(from),
        new Date(to),
        controller,
      )
        .then((res) => {
          const data = res.data;
          if (data) {
            const assetsHistoricalData: { [k: string]: chartDataType[] } = {};
            const symbolWithData = symbols.filter(
              (symbol) => data[symbol]?.values,
            );
            setValidSymbols(
              symbolWithData.map((symbol) =>
                symbol.split("/")[0].toLowerCase(),
              ),
            );
            symbolWithData.forEach((symbol) => {
              const assetData = data[symbol];
              const values = assetData.values
                .map((value: any) => {
                  const item: chartDataType = {
                    value: parseFloat(value.open),
                    close: parseFloat(value.close),
                    time: (applyLocalTimezoneOffset(
                      new Date(value.datetime).getTime(),
                    ) / 1000) as chartDataType["time"],
                    high: parseFloat(value.high),
                    low: parseFloat(value.low),
                    open: parseFloat(value.open),
                  };
                  return item;
                })
                .reverse();
              assetsHistoricalData[symbol] = values;
            });

            setAssetsHistoricalData(assetsHistoricalData);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          if (error.code !== "ERR_CANCELED" && error.code != null) {
            setIsLoading(false);
          }
        });
    }

    return () => {
      controller.abort();
    };
  }, [SA?.assets, customDuration, from, getAssetSymbol, setIsLoading, to]);

  const calculateLineChartsData = useCallback(() => {
    if (assetsHistoricalData && SA?.assets?.length) {
      const holdingsWithHistoricalData = SA.assets.filter((holding) =>
        validSymbols.includes(
          holding.asset_details?.asset_name?.toLowerCase() || "",
        ),
      );
      const currentWeightsPoints: chartDataType[] = [];
      const setWeightsPoints: chartDataType[] = [];

      let currentWeightsDrawdown = initialValue;
      let setWeightsDrawdown = initialValue;

      const numberOfPoints = Object.values(assetsHistoricalData)
        .map((a) => a.length)
        .sort()
        .reverse()[0];

      const quantities: {
        [k: string]: {
          currentWeightQuantity: number;
          setWeightQuantity: number;
        };
      } = {};

      if (holdingsWithHistoricalData.length) {
        const totalCurrentWeight =
          holdingsWithHistoricalData
            ?.map((a) => a.current_weight)
            ?.reduce((prev, curr) => (prev ?? 0) + (curr ?? 0)) ?? 1;
        const totalSetWeight =
          holdingsWithHistoricalData
            ?.map((a) => a.weight)
            ?.reduce((prev, curr) => (prev ?? 0) + (curr ?? 0)) ?? 1;

        holdingsWithHistoricalData.forEach((holding) => {
          const currentWeight =
            (holding.current_weight ?? 0) / totalCurrentWeight;
          const setWeight = (holding.weight ?? 0) / totalSetWeight;

          const assetSymbol = getAssetSymbol(holding);
          if (!assetsHistoricalData[assetSymbol]) {
            return;
          }
          const firstPointValue =
            assetsHistoricalData[assetSymbol][0].value ?? 0;

          quantities[assetSymbol] = {
            currentWeightQuantity:
              (currentWeight * initialValue) / firstPointValue,
            setWeightQuantity: (setWeight * initialValue) / firstPointValue,
          };
        });

        for (let pointIndex = 0; pointIndex < numberOfPoints; pointIndex++) {
          let currentWeightValue = 0;
          let setWeightValue = 0;
          let time: Time = 0 as UTCTimestamp;

          Object.keys(quantities).forEach((assetSymbol) => {
            const currentWeightQuantity =
              quantities[assetSymbol].currentWeightQuantity;
            const setWeightQuantity = quantities[assetSymbol].setWeightQuantity;

            if (
              !isNaN(currentWeightQuantity) &&
              currentWeightQuantity !== Infinity
            )
              currentWeightValue +=
                (assetsHistoricalData[assetSymbol][pointIndex]?.value ?? 0) *
                currentWeightQuantity;

            if (!isNaN(setWeightQuantity) && setWeightQuantity !== Infinity)
              setWeightValue +=
                (assetsHistoricalData[assetSymbol][pointIndex]?.value ?? 0) *
                setWeightQuantity;

            if (
              assetsHistoricalData[assetSymbol][pointIndex]?.time &&
              time === 0
            )
              time = assetsHistoricalData[assetSymbol][pointIndex]?.time;
          });

          if (currentWeightValue < currentWeightsDrawdown) {
            currentWeightsDrawdown = currentWeightValue;
          }

          if (setWeightValue < setWeightsDrawdown) {
            setWeightsDrawdown = setWeightValue;
          }

          currentWeightsPoints.push({ time, value: currentWeightValue });
          setWeightsPoints.push({ time, value: setWeightValue });
        }

        setCurrentWeightsData(
          currentWeightsPoints
            .sort((a, b) => (a.time > b.time ? 1 : -1))
            .filter((obj, index) => {
              return (
                index ===
                currentWeightsPoints.findIndex((o) => obj.time === o.time)
              );
            }),
        );
        setSetWeightsData(setWeightsPoints);
      }
    }
  }, [
    SA?.assets,
    assetsHistoricalData,
    getAssetSymbol,
    initialValue,
    validSymbols,
  ]);

  useEffect(() => calculateLineChartsData(), [calculateLineChartsData]);

  const graphLegend = useMemo(() => {
    return (
      <div className="z-0 flex w-full h-10 gap-4 bg-secondary px-2 justify-between items-center">
        {actions}
        <div className="flex flex-col md:flex-row items-start md:items-center md:gap-4">
          <div className="flex items-center justify-center gap-2">
            <div className="h-2 w-2 bg-[#16a34a] rounded-full" />
            <p className="text-xs font-bold">{m.back_test_current_weight()}</p>
          </div>
          <div className="flex items-center justify-center gap-2">
            <div className="h-2 w-2 bg-[#f59e0b]  rounded-full" />
            <p className="text-xs font-bold">{m.back_test_set_weight()}</p>
          </div>
        </div>
      </div>
    );
  }, [actions]);

  const graphChart = useMemo(() => {
    const cond = !SA?.assets.length || !currentWeightsData.length;
    return (
      <div
        className={twMerge(
          cond ? "" : "pt-5 gap-2.5",
          "z-0 flex flex-col w-full border rounded-sm overflow-hidden min-h-[400px] max-w-full",
        )}
      >
        {cond ? (
          <div className="relative">
            <Skeleton className="h-[360px] w-full rounded-none" />
            <div className="absolute flex items-center justify-center top-0 bottom-0 left-0 right-0">
              <p className="text-xl font-mono">{m.ok_front_grebe_trip()}</p>
            </div>
          </div>
        ) : (
          <LineChart
            primaryLineData={currentWeightsData}
            secondaryLineData={SA?.exists ? setWeightsData : []}
            className="z-0 h-full min-h-[300px]"
            fixed={true}
            isLoading={isLoading}
            tooltip={{
              show: true,
              title: "Profit Change %",
              showProfitChange: true,
            }}
          />
        )}
        {graphLegend}
      </div>
    );
  }, [
    SA?.assets.length,
    SA?.exists,
    currentWeightsData,
    graphLegend,
    isLoading,
    setWeightsData,
  ]);

  return graphChart;
}
